import { createApi } from "@reduxjs/toolkit/query/react";
import { ApiEndpoints, baseQueryWithAuth } from "../../core";
import {
  AddonScreensRequest,
  AddonScreensResponse,
  AddScreensRequest,
  AddScreensResponse,
  PlansResponse,
  TaxesResponse,
} from "../../types";
import { toast } from "../../core/toast";
import { setSubscriptionSummary } from "../../redux/licenseReducer";

export const subscriptionLicenseApi = createApi({
  reducerPath: "subscriptionLicenseApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: [
    "SubscriptionLicenses",
    "LicensesAddon",
    "SubscriptionScreens"
  ],
  endpoints: (builder) => ({
    addLicenses: builder.mutation<AddScreensResponse, AddScreensRequest>({
      query: (body) => ({
        url: ApiEndpoints.addSubscriptionLicenses,
        method: "POST",
        body,
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data?.message || "License added successfully");
          dispatch(
            subscriptionLicenseApi.util.invalidateTags(["SubscriptionLicenses"])
          );
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error adding license");
        }
      },
      invalidatesTags: ["SubscriptionLicenses", "SubscriptionScreens"],
    }),
    addOnLicenses: builder.mutation<AddonScreensResponse, AddonScreensRequest>({
      query: (body) => {
        const { licenseId, ...payload } = body as any;
        return {
          url: `${ApiEndpoints.addOnSubscriptionLicenses}/${licenseId}`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          // toast.success(data?.message || "Addon license added successfully");
          dispatch(
            subscriptionLicenseApi.util.invalidateTags(["SubscriptionLicenses"])
          );
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error adding addon license");
        }
      },
      invalidatesTags: ["SubscriptionLicenses", "LicensesAddon", "SubscriptionScreens"],
    }),
    getPlans: builder.query<PlansResponse, | void>({
      query: () => {
        return {
          url: `${ApiEndpoints.getPlans}?limit=-1`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // toast.success(data?.message || "Success");
        } catch (err: any) {
          // toast.error(err?.error?.data?.message || "Error");
        }
      },
    }),
    getTaxes: builder.query<TaxesResponse, void>({
      query: () => ({
        url: ApiEndpoints.getTaxes,
        method: "GET",
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // toast.success(data?.message || "Success");
        } catch (err: any) {
          // toast.error(err?.error?.data?.message || "Error");
        }
      },
    }),
    getSubscriptionsSummary: builder.query<any, { organisationId: string } | void>({
      query: (args) => {
        const { organisationId } = (args || {}) as {
          organisationId: string;
        };
        return {
          url: `${ApiEndpoints.getSubscription}?organisation=${organisationId}`,
          method: "GET",
        };
      },
      providesTags: (result) =>
        result && Array.isArray(result.results)
          ? [
            ...result.results.map((r: any) => ({
              type: "SubscriptionLicenses" as const,
              id: r._id,
            })),
            { type: "SubscriptionLicenses" as const, id: "LIST" },
          ]
          : [{ type: "SubscriptionLicenses" as const, id: "LIST" }],
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          // toast.success(data?.message || "Success");
          dispatch(setSubscriptionSummary(data));
        } catch (err: any) {
          // toast.error(err?.error?.data?.message || "Error");
        }
      },
    }),

    getLicenses: builder.query<any, { organisationId: string; page?: number; limit?: number; gymId?: string; isAssigned?: boolean } | void>({
      query: (args) => {
        const { organisationId, page = 1, limit = 10, gymId, isAssigned } = (args || {}) as {
          organisationId: string;
          page?: number;
          limit?: number;
          gymId?: string;
          isAssigned?: boolean;
        };

        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("limit", String(limit));
        if (gymId) params.set("gym", gymId);
        if (isAssigned !== undefined) params.set("isAssigned", String(isAssigned));

        return {
          url: `${ApiEndpoints.getSubscriberLicenses}?organisation=${organisationId}&${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: (result) =>
        result && Array.isArray(result.results)
          ? [
            ...result.results.map((r: any) => ({
              type: "SubscriptionLicenses" as const,
              id: r._id,
            })),
            { type: "SubscriptionLicenses" as const, id: "LIST" },
          ]
          : [{ type: "SubscriptionLicenses" as const, id: "LIST" }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // toast.success(data?.message || "Success");
        } catch (err: any) {
          // toast.error(err?.error?.data?.message || "Error");
        }
      },
    }),
    getOrganisationScreens: builder.query<any, { organisationId: string; page?: number; limit?: number } | void>({
      query: (args) => {
        const { organisationId, page = 1, limit = -1 } = (args || {}) as {
          organisationId: string;
          page?: number;
          limit?: number;
        };

        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("limit", String(limit));

        return {
          url: `${ApiEndpoints.getOrganisationScreens}/${organisationId}?${params.toString()}`,
          method: "GET",
        };
      },
      extraOptions: { hideToast: true },
      providesTags: (result) =>
        result && Array.isArray(result.results)
          ? [
            ...result.results.map((r: any) => ({
              type: "SubscriptionScreens" as const,
              id: r._id,
            })),
            { type: "SubscriptionScreens" as const, id: "LIST" },
          ]
          : [{ type: "SubscriptionScreens" as const, id: "LIST" }],
    }),
    downgradeLicense: builder.mutation<
      any,
      { licenseId: string; planId: string }
    >({
      query: ({ licenseId, planId }) => ({
        url: `${ApiEndpoints.downgradeSubscriberLicense}/${licenseId}/plan/${planId}`,
        method: "PATCH",
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          // toast.success(data?.message || "License downgraded successfully");
          dispatch(
            subscriptionLicenseApi.util.invalidateTags(["SubscriptionLicenses"])
          );
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error downgrading license");
        }
      },
      invalidatesTags: ["SubscriptionLicenses", "SubscriptionScreens"],
    }),
    upgradeLicense: builder.mutation<
      any,
      { licenseId: string; planId: string }
    >({
      query: ({ licenseId, planId }) => ({
        url: `${ApiEndpoints.upgradeSubscriberLicense}/${licenseId}/plan/${planId}`,
        method: "PATCH",
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          // toast.success(data?.message || "License upgraded successfully");
          dispatch(
            subscriptionLicenseApi.util.invalidateTags(["SubscriptionLicenses"])
          );
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error upgrading license");
        }
      },
      invalidatesTags: ["SubscriptionLicenses", "SubscriptionScreens"],
    }),
    cancelLicense: builder.mutation<any, { licenseId: string }>({
      query: ({ licenseId }) => ({
        url: `${ApiEndpoints.cancelSubscriberLicense}/${licenseId}`,
        method: "PATCH",
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          // toast.success(data?.message || "License cancelled successfully");
          dispatch(
            subscriptionLicenseApi.util.invalidateTags(["SubscriptionLicenses"])
          );
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error cancelling license");
        }
      },
      invalidatesTags: ["SubscriptionLicenses", "SubscriptionScreens"],
    }),
    cancelAddonScreen: builder.mutation<any, { addonScreenId: string }>({
      query: ({ addonScreenId }) => ({
        url: `${ApiEndpoints.cancelSubscriberScreen}/${addonScreenId}`,
        method: "PATCH",
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          // toast.success(data?.message || "Addon screen deleted successfully");
          dispatch(
            subscriptionLicenseApi.util.invalidateTags(["SubscriptionScreens"])
          );
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error deleting addon screen");
        }
      },
      invalidatesTags: ["SubscriptionScreens", "SubscriptionLicenses"],
    }),
    resubscribeLicense: builder.mutation<any, { licenseId: string }>({
      query: ({ licenseId }) => ({
        url: `${ApiEndpoints.resubscribeSubscriberLicense}/${licenseId}`,
        method: "PATCH",
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          // toast.success(data?.message || "License resubscribed successfully");
          dispatch(
            subscriptionLicenseApi.util.invalidateTags(["SubscriptionLicenses"])
          );
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error resubscribing license");
        }
      },
      invalidatesTags: ["SubscriptionLicenses", "SubscriptionScreens"],
    }),
    resubscribeScreen: builder.mutation<any, { screenId: string }>({
      query: ({ screenId }) => ({
        url: `${ApiEndpoints.resubscribeSubscriberScreen}/${screenId}`,
        method: "PATCH",
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          // toast.success(data?.message || "License resubscribed successfully");
          dispatch(
            subscriptionLicenseApi.util.invalidateTags(["SubscriptionLicenses"])
          );
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error resubscribing license");
        }
      },
      invalidatesTags: ["SubscriptionLicenses", "SubscriptionScreens"],
    }),
    revokeLicense: builder.mutation<any, { licenseId: string }>({
      query: ({ licenseId }) => ({
        url: `${ApiEndpoints.revokeSubscriberLicense}/${licenseId}`,
        method: "PATCH",
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          // toast.success(data?.message || "License revoked successfully");
          dispatch(
            subscriptionLicenseApi.util.invalidateTags(["SubscriptionLicenses"])
          );
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error revoking license");
        }
      },
      invalidatesTags: ["SubscriptionLicenses", "SubscriptionScreens"],
    }),
    revokeScreen: builder.mutation<any, { screenId: string }>({
      query: ({ screenId }) => ({
        url: `${ApiEndpoints.revokeSubscriberScreen}/${screenId}`,
        method: "PATCH",
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          // toast.success(data?.message || "Screen revoked successfully");
          dispatch(
            subscriptionLicenseApi.util.invalidateTags(["SubscriptionScreens"])
          );
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error revoking screen");
        }
      },
      invalidatesTags: ["SubscriptionScreens", "SubscriptionLicenses"],
    }),
    cancelPendingSubscription: builder.mutation<any, { subscriptionId: string }>({
      query: ({ subscriptionId }) => ({
        // If you add this to your ApiEndpoints object, you can swap this string out for:
        // url: `${ApiEndpoints.cancelPendingSubscription}/${subscriptionId}`
        url: `${ApiEndpoints.cancelPendingSubscription}/${subscriptionId}`,
        method: "PATCH", // Change to "POST" or "DELETE" if your backend expects a different method
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          // toast.success(data?.message || "Pending subscription cancelled successfully");
          toast.success(data?.message || "Pending subscription cancelled successfully!");
          // Invalidate tags to trigger a refetch of your list
          dispatch(
            subscriptionLicenseApi.util.invalidateTags(["SubscriptionLicenses"])
          );
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error cancelling pending subscription");
        }
      },
      // Ensure the relevant lists are updated after cancellation
      invalidatesTags: ["SubscriptionLicenses", "SubscriptionScreens"],
    }),

    downloadRazorpayInvoice: builder.mutation<Blob, { invoiceId: string | undefined }>({
      query: ({ invoiceId }) => ({
        url: `subscriber-service/api/v1/invoice/${invoiceId}/download`,
        method: "GET",
        responseHandler: (response) => response.blob(), // 🔥 important
      }),

      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          // ✅ Create download link
          const url = window.URL.createObjectURL(data);
          const a = document.createElement("a");
          a.href = url;
          a.download = `invoice-${arg.invoiceId}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();

          window.URL.revokeObjectURL(url);

          toast.success("Invoice downloaded successfully");
        } catch (err: any) {
          toast.error(
            err?.error?.data?.message || "Failed to download invoice"
          );
        }
      },
    }),



  }),
});

export const {
  useAddLicensesMutation,
  useAddOnLicensesMutation,
  useGetPlansQuery,
  useGetTaxesQuery,
  useGetSubscriptionsSummaryQuery,
  useGetLicensesQuery,
  useGetOrganisationScreensQuery,
  useDowngradeLicenseMutation,
  useUpgradeLicenseMutation,
  useCancelLicenseMutation,
  useCancelAddonScreenMutation,
  useResubscribeLicenseMutation,
  useResubscribeScreenMutation,
  useRevokeLicenseMutation,
  useRevokeScreenMutation,
  useCancelPendingSubscriptionMutation,
  useDownloadRazorpayInvoiceMutation,
} = subscriptionLicenseApi;
